import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Chip,
  Button, Alert, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Tooltip } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import { getIndexDetail, listCollectionFiles, downloadCollectionFile } from "../api/agentApi";
import UpdateIndexButton from './DocumentIndexDetails/UpdateIndexButton.tsx';
import DetailItem from './AgentDetail/DetailItem.tsx';
import { v4 as uuidv4 } from 'uuid';

const POLL_INTERVAL_BASE = 5000; // ms
const POLL_INTERVAL_MAX = 60000; // ms

function fetchIndexById(id) {
  return getIndexDetail(id).then(data => {
    if (!data || !data.index_id) throw new Error("Collection not found");
    return data;
  });
}

// Helper function to extract uploadId from source path
// Expected format: .../rawdata/41b08581-2b6b-47b9-af57-baa03c8168d0/...
function extractUploadIdFromSourcePath(sourcePath) {
  if (!sourcePath || !Array.isArray(sourcePath) || sourcePath.length === 0) {
    return null;
  }
  
  // Get the first source path (could be object or string)
  const firstPath = typeof sourcePath[0] === 'object' ? sourcePath[0].path : sourcePath[0];
  
  if (!firstPath) return null;
  
  // Match UUID pattern after .rawdata/
  const match = firstPath.match(/\.rawdata\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  
  return match ? match[1] : null;
}

export default function KnowledgeBuilderDetailPage() {
  const { type, id } = useParams();
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileCache, setFileCache] = useState({}); // Cache fetched files

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    
    // Calculate which batch of 100 files we need
    const newSkip = Math.floor((newPage * rowsPerPage) / 100) * 100;
    const currentSkip = Math.floor((page * rowsPerPage) / 100) * 100;
    
    // If we need a different batch, fetch it
    if (newSkip !== currentSkip) {
      await fetchFilesForPage(newSkip);
    }
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Fetch first batch if not cached
    if (!fileCache[0]) {
      await fetchFilesForPage(0);
    }
  };

  // Fetch files for a specific skip offset
  const fetchFilesForPage = async (skip) => {
    if (!index || !index.source_path || !Array.isArray(index.source_path)) {
      console.log('No collection or source_path available:', { index, source_path: index?.source_path });
      return;
    }

    // Check if we already have this batch cached
    if (fileCache[skip]) {
      console.log(`Using cached files for skip=${skip}`);
      return;
    }

    console.log(`Fetching files for skip=${skip}, collection source_paths:`, index.source_path);
    setLoadingFiles(true);
    const batchFiles = [];
    let total = 0;

    for (const src of index.source_path) {
      const path = src.path || '';
      const uuidMatch = path.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      
      if (uuidMatch) {
        const uploadId = uuidMatch[1];
        console.log(`Found uploadId: ${uploadId}, fetching files...`);
        try {
          const response = await listCollectionFiles(uploadId, skip, 100);
          if (response && response.files) {
            response.files.forEach(file => {
              batchFiles.push({
                ...file,
                status: src.status,
                message: src.message,
                uploadId: uploadId
              });
            });
            // Use total from first response (assumes all uploads have same total - adjust if needed)
            if (total === 0) {
              total = response.total;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch files for ${uploadId}:`, error);
        }
      } else {
        console.log('No UUID found in path:', path);
      }
    }

    console.log(`Fetched ${batchFiles.length} files, total: ${total}`);
    // Update cache
    setFileCache(prev => ({ ...prev, [skip]: batchFiles }));
    setTotalFiles(total);
    setLoadingFiles(false);
  };

  // Fetch initial files when index loads
  useEffect(() => {
    if (index) {
      console.log('Collection loaded, fetching initial files...');
      fetchFilesForPage(0);
    }
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get files for current page from cache
  const getCurrentPageFiles = () => {
    const skip = Math.floor((page * rowsPerPage) / 100) * 100;
    const cachedBatch = fileCache[skip] || [];
    
    // Calculate offset within the cached batch
    const offsetInBatch = (page * rowsPerPage) % 100;
    return cachedBatch.slice(offsetInBatch, offsetInBatch + rowsPerPage);
  };

  const paginatedFiles = getCurrentPageFiles();

  const handleDownloadFile = async (file) => {
    try {
      // Use the backend API proxy to serve the file
      const blob = await downloadCollectionFile(file.uploadId, file.path);
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const refreshIndex = async () => {
    try {
      const idx = await fetchIndexById(id);
      setIndex(idx);
      
      // Extract uploadId from source path if not already set
      if (!uploadId && idx.source_path) {
        const extractedUploadId = extractUploadIdFromSourcePath(idx.source_path);
        if (extractedUploadId) {
          setUploadId(extractedUploadId);
        } else {
          // Generate new UUID if extraction failed
          setUploadId(uuidv4());
        }
      }
    } catch (e) {
      console.error("Failed to refresh index:", e);
    }
  };

  useEffect(() => {
    let poller;
    let isMounted = true;
    const fetchIndex = async () => {
      try {
        const idx = await fetchIndexById(id);
        if (!isMounted) return;
        setIndex(idx);
        setLoading(false);
        
        // Extract uploadId from source path on initial load
        if (idx.source_path) {
          const extractedUploadId = extractUploadIdFromSourcePath(idx.source_path);
          if (extractedUploadId) {
            setUploadId(extractedUploadId);
          } else {
            // Generate new UUID if extraction failed
            setUploadId(uuidv4());
          }
        } else {
          // No source path, generate new UUID
          setUploadId(uuidv4());
        }
        
        // Only poll every 5s if status is 'running'
        if (idx.status === 'running') {
          setIsPolling(true);
          poller = setTimeout(fetchIndex, POLL_INTERVAL_BASE);
        } else {
          setIsPolling(false);
        }
      } catch (e) {
        if (!isMounted) return;
        setError(e.message || "Failed to fetch index");
        setLoading(false);
        setIsPolling(false);
      }
    };
    // Only show spinner on first load
    if (!index) setLoading(true);
    fetchIndex();
    return () => {
      isMounted = false;
      if (poller) clearTimeout(poller);
    };
  }, [id]);

  if (loading) {
    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 800, mx: "auto", my: 8, p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box component={Paper} elevation={3} sx={{ maxWidth: 800, mx: "auto", my: 8, p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!index) {
    return null;
  }

  return (
    <Box >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",  mb: 2, pr: 3, mt: 2 }}>
        <Typography variant="h4">Collection: {index.name}</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
        {uploadId && (
          <UpdateIndexButton 
            indexId={index.index_id}
            indexName={index.name}
            indexDetails={index}
            uploadId={uploadId}
            variant="outlined"
            onUpdateComplete={refreshIndex}
          />
        )}
        <Button component={Link} to="/kb" variant="outlined" sx={{ ml: 2 }}>
          Back to Collections
        </Button>
        </Box>
      </Box>
      <Box sx={{pt: 2}}>
      <DetailItem label="Status" variant="h6" value={
              <Chip
                    label={index.status ? index.status.charAt(0).toUpperCase() + index.status.slice(1) : 'Unknown'}
                    sx={{
                      bgcolor: index.status === 'completed'
                        ? '#43a047'
                        : index.status === 'running'
                          ? '#ffb300'
                          : index.status === 'processing'
                            ? '#1976d2'
                            : index.status === 'failed'
                              ? '#d32f2f'
                              : '#e0e0e0',
                       color: index.status === 'completed' ? '#fff' : '#333',
                      fontWeight: 500,
                      borderRadius: "9999px", // fully rounded
                      height: 24,
                      px: 1.5,
                    }}
                />
            }
        />
      </Box>
      <Box sx={{ mx: "auto"}}>
        {/* Uploaded Files List */}
        <Box sx={{ mb: 3 }}>
          <Accordion
            defaultExpanded={totalFiles > 0}
            elevation={1}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsertDriveFile />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Uploaded Files ({totalFiles})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {loadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>Loading files...</Typography>
                </Box>
              ) : totalFiles > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedFiles.map((file, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Download file">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDownloadFile(file)}
                                    sx={{ color: 'primary.main', p: 0.5 }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Typography variant="body2">
                                  {file.filename}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={totalFiles}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No files uploaded yet.
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box sx={{ mb: 0}}>
            <DetailItem label="Index ID" value={index.index_id} />
            <DetailItem label="Type" value={index.type === 'doc' ? 'Document Collection' : 'Code Collection'} />
            <DetailItem label="Description" value={index.description || '--'} />
            <DetailItem label="Created" value={index.created_at ? new Date(index.created_at).toLocaleString() : '--'} />
            <DetailItem label="Updated" value={index.updated_at ? new Date(index.updated_at).toLocaleString() : '--'} />
            <DetailItem label="Message" value={index.message ? index.message.replace(/indexing/gi, 'processing') : '--'} />
        </Box>
      </Box>
    </Box>
  );
}
